import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { LoginPage } from "./pages/LoginPage";
import { FeedPage } from "./pages/FeedPage";
import { ExplorePage } from "./pages/ExplorePage";
import { ActivityPage } from "./pages/ActivityPage";
import { AccountPage } from "./pages/AccountPage";
import { UserProfilePage } from "./pages/UserProfilePage";
import { AdminPage } from "./pages/AdminPage";
import { AppShell } from "./components/AppShell";
import { authService } from "./services/auth";
import { apiService } from "./services/api";
import { analyticsService } from "./services/analytics";
import { TooltipProvider } from "./components/radix/Tooltip";
import { SignInModalProvider } from "./context/SignInModalContext";
import "./App.css";

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Initialize auth service (sets up token refresh)
        await authService.initialize();

        // Check for OAuth callback
        const hasTokens = await authService.handleCallback();

        if (hasTokens) {
          setIsAuthenticated(true);
          setIsChecking(false);
          return;
        }

        // Check if already authenticated
        const authenticated = authService.isAuthenticated();
        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const userInfo = authService.getUserInfo();
      analyticsService.trackAppOpen(userInfo?.sub, userInfo?.email);
      if (userInfo?.picture) {
        apiService.syncProfileImage().catch(() => {});
      }
    }
  }, [isAuthenticated]);

  if (isChecking) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  const userInfo = authService.getUserInfo();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/feed" replace /> : <LoginPage />
        }
      />
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/feed" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="*"
        element={
          <AppShell
            userInfo={userInfo}
            onCreatePoll={() => setShowCreateForm(true)}
            showTabs={true}
          >
            <Routes>
              {/* Account page (logged-in user's account) */}
              <Route
                path="/account"
                element={
                  isAuthenticated ? (
                    <AccountPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Admin moderation page */}
              <Route
                path="/admin"
                element={
                  isAuthenticated ? (
                    <AdminPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* User profile page (other user's profile) */}
              <Route
                path="/user/:userId"
                element={
                  <UserProfilePage
                    userId=""
                    showCreateForm={showCreateForm}
                    onShowCreateFormChange={setShowCreateForm}
                  />
                }
              />

              {/* Main feed routes */}
              <Route
                path="/feed"
                element={
                  <FeedPage
                    showCreateForm={showCreateForm}
                    onShowCreateFormChange={setShowCreateForm}
                  />
                }
              />
              <Route
                path="/feed/:pollId"
                element={
                  <FeedPage
                    showCreateForm={showCreateForm}
                    onShowCreateFormChange={setShowCreateForm}
                  />
                }
              />

              {/* Other pages */}
              <Route
                path="/explore"
                element={
                  <ExplorePage
                    showCreateForm={showCreateForm}
                    onShowCreateFormChange={setShowCreateForm}
                  />
                }
              />
              <Route
                path="/activity"
                element={
                  <ActivityPage
                    showCreateForm={showCreateForm}
                    onShowCreateFormChange={setShowCreateForm}
                  />
                }
              />

              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/feed" replace />} />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SignInModalProvider>
        <TooltipProvider>
          <AppContent />
        </TooltipProvider>
      </SignInModalProvider>
    </BrowserRouter>
  );
}

export default App;
